import { useState } from 'react'
import type { PerRuleMetric, TestRun } from '@sophia/shared-schemas'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { cn } from '@/lib/utils'
import { PrecisionHint } from './PrecisionHint'
import { explainRulePrecision } from '../lib/precision-explainer'

interface Props {
  run: TestRun
  classFilter: string | null
}

function pct(v: number, digits = 1): string {
  if (Number.isNaN(v)) return '—'
  return `${(v * 100).toFixed(digits)}%`
}

function confidenceTone(t: PerRuleMetric['confidence']): 'success' | 'warning' | 'danger' {
  if (t === 'ALTA') return 'success'
  if (t === 'MEDIA') return 'warning'
  return 'danger'
}

function RuleRow({ rule, expanded, onToggle }: { rule: PerRuleMetric; expanded: boolean; onToggle: () => void }) {
  const precisionExplanation = explainRulePrecision({
    matches: rule.matches,
    correctMatches: rule.correctMatches,
  })
  const precisionValue = precisionExplanation?.kind === 'unavailable' ? '—' : pct(rule.precision)
  return (
    <>
      <tr className="hover:bg-bg-subtle cursor-pointer" onClick={onToggle}>
        <td className="px-3 py-2.5 w-6">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-fg-3" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-fg-3" />
          )}
        </td>
        <td className="px-3 py-2.5">
          <div className="text-sm font-medium text-fg-1">{rule.ruleName}</div>
          <div className="text-2xs text-fg-3 data-mono">{rule.className} · peso {rule.weight}</div>
        </td>
        <td className="px-3 py-2.5 text-right data-mono text-sm text-fg-2">{rule.matches}</td>
        <td className="px-3 py-2.5 text-right data-mono text-sm text-success">{rule.correctMatches}</td>
        <td className="px-3 py-2.5 text-right data-mono text-sm text-danger">{rule.falsePositives}</td>
        <td className="px-3 py-2.5 text-right data-mono text-sm text-fg-2">
          <span className="inline-flex items-center justify-end gap-1.5">
            {precisionValue}
            <PrecisionHint explanation={precisionExplanation} compact />
          </span>
        </td>
        <td className="px-3 py-2.5 text-right data-mono text-sm text-fg-2">{pct(rule.coverage)}</td>
        <td className="px-3 py-2.5 text-right">
          <Badge tone={confidenceTone(rule.confidence)}>{rule.confidence}</Badge>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="px-3 pb-4 bg-bg-subtle">
            {precisionExplanation && (
              <div className="mt-2">
                <PrecisionHint explanation={precisionExplanation} />
              </div>
            )}
            <div className="text-2xs uppercase tracking-wider text-fg-3 mt-2 mb-1">Falsos positivos por classe</div>
            {rule.fpBreakdown.length === 0 ? (
              <p className="text-xs text-fg-3 italic">Nenhum falso positivo nesta run.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {rule.fpBreakdown.map((b, i) => (
                  <li key={i} className="text-xs text-fg-2 flex items-center gap-2">
                    <span className="data-mono text-danger">{b.count}</span>
                    <span>→ classificadas como</span>
                    <span className="font-medium">{rule.className}</span>
                    <span>mas eram</span>
                    <span className="font-medium">{b.className}</span>
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

export function RulesTab({ run, classFilter }: Props) {
  const metrics = run.metrics
  const [expandedId, setExpandedId] = useState<string | null>(null)
  if (!metrics) return <p className="text-sm text-fg-3">Sem métricas.</p>

  const rules = classFilter ? metrics.perRule.filter((r) => r.classId === classFilter) : metrics.perRule

  return (
    <Card>
      <CardHeader>
        <h3 className="font-display font-bold text-base text-fg-1">
          Performance por regra{' '}
          <span className="text-fg-3 font-normal data-mono text-sm">({rules.length})</span>
        </h3>
        <p className="text-sm text-fg-3 mt-0.5">Ordenadas por precisão. Clique numa regra pra ver o FP breakdown.</p>
      </CardHeader>
      <CardBody className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-2xs uppercase tracking-wider text-fg-3 border-b border-border-subtle">
              <th></th>
              <th className="px-3 py-2 text-left font-semibold">Regra · Classe</th>
              <th className="px-3 py-2 text-right font-semibold">Matches</th>
              <th className="px-3 py-2 text-right font-semibold">Corretos</th>
              <th className="px-3 py-2 text-right font-semibold">FP</th>
              <th className="px-3 py-2 text-right font-semibold">Precisão</th>
              <th className="px-3 py-2 text-right font-semibold">Cobertura</th>
              <th className="px-3 py-2 text-right font-semibold">Confiança</th>
            </tr>
          </thead>
          <tbody className={cn('divide-y divide-border-subtle')}>
            {rules.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center text-sm text-fg-3 italic">
                  Nenhuma regra {classFilter ? 'pra essa classe' : ''}.
                </td>
              </tr>
            ) : (
              rules.map((r) => (
                <RuleRow
                  key={r.ruleId}
                  rule={r}
                  expanded={expandedId === r.ruleId}
                  onToggle={() => setExpandedId((cur) => (cur === r.ruleId ? null : r.ruleId))}
                />
              ))
            )}
          </tbody>
        </table>
      </CardBody>
    </Card>
  )
}
